import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { recoveredErrorTracker } from './recoveredErrorTracker';
import { getBuildTimestamp } from './build-info';

/**
 * Safe Mode disables fancy portal-based UI components (Select, DatePicker, Popover)
 * and uses native HTML elements instead. This prevents DOM timing crashes on
 * slower systems or problematic browser configurations.
 *
 * Safe Mode can be:
 * - Manually enabled by user in Settings
 * - Auto-enabled when too many errors are recovered
 * - Temporarily enabled for a session
 *
 * Auto-disable conditions:
 * - After 24 hours of being enabled
 * - On new app version/release
 */

/** How long safe mode stays auto-enabled before auto-disabling (24 hours) */
const AUTO_DISABLE_TIMEOUT_MS = 24 * 60 * 60 * 1000;

interface SafeModeState {
  /** Whether safe mode is currently active */
  enabled: boolean;

  /** Whether safe mode was auto-enabled due to errors */
  autoEnabled: boolean;

  /** Timestamp when auto-enabled (for reset logic) */
  autoEnabledAt: string | null;

  /** Count of errors that triggered auto-enable */
  triggerErrorCount: number;

  /** App version when safe mode was enabled (for release-based reset) */
  enabledAtVersion: string | null;

  /** Enable safe mode manually */
  enable: () => void;

  /** Disable safe mode manually */
  disable: () => void;

  /** Toggle safe mode */
  toggle: () => void;

  /** Auto-enable due to frequent errors */
  autoEnable: (errorCount: number) => void;

  /** Reset auto-enable (e.g., on new session or after time passes) */
  resetAutoEnable: () => void;

  /** Check and apply auto-disable conditions */
  checkAutoDisable: () => void;
}

export const useSafeMode = create<SafeModeState>()(
  persist(
    (set, get) => ({
      enabled: false,
      autoEnabled: false,
      autoEnabledAt: null,
      triggerErrorCount: 0,
      enabledAtVersion: null,

      enable: () =>
        set({
          enabled: true,
          enabledAtVersion: getBuildTimestamp(),
        }),

      disable: () =>
        set({
          enabled: false,
          autoEnabled: false,
          autoEnabledAt: null,
          enabledAtVersion: null,
        }),

      toggle: () => {
        const state = get();
        if (state.enabled) {
          set({
            enabled: false,
            autoEnabled: false,
            autoEnabledAt: null,
            enabledAtVersion: null,
          });
        } else {
          set({
            enabled: true,
            enabledAtVersion: getBuildTimestamp(),
          });
        }
      },

      autoEnable: (errorCount: number) => {
        if (!get().enabled) {
          console.warn(`[SafeMode] Auto-enabling due to ${errorCount} recovered errors`);
          set({
            enabled: true,
            autoEnabled: true,
            autoEnabledAt: new Date().toISOString(),
            triggerErrorCount: errorCount,
            enabledAtVersion: getBuildTimestamp(),
          });
        }
      },

      resetAutoEnable: () => {
        const state = get();
        if (state.autoEnabled) {
          set({
            enabled: false,
            autoEnabled: false,
            autoEnabledAt: null,
            triggerErrorCount: 0,
            enabledAtVersion: null,
          });
        }
      },

      checkAutoDisable: () => {
        const state = get();
        if (!state.enabled) return;

        const currentVersion = getBuildTimestamp();

        // Auto-disable on new version/release
        if (state.enabledAtVersion && state.enabledAtVersion !== currentVersion) {
          console.info(
            `[SafeMode] Auto-disabling due to new release (${state.enabledAtVersion} → ${currentVersion})`
          );
          set({
            enabled: false,
            autoEnabled: false,
            autoEnabledAt: null,
            triggerErrorCount: 0,
            enabledAtVersion: null,
          });
          return;
        }

        // Auto-disable after timeout (only for auto-enabled, not manual)
        if (state.autoEnabled && state.autoEnabledAt) {
          const enabledTime = new Date(state.autoEnabledAt).getTime();
          const elapsed = Date.now() - enabledTime;

          if (elapsed > AUTO_DISABLE_TIMEOUT_MS) {
            console.info(
              `[SafeMode] Auto-disabling after ${Math.round(elapsed / 1000 / 60 / 60)} hours`
            );
            set({
              enabled: false,
              autoEnabled: false,
              autoEnabledAt: null,
              triggerErrorCount: 0,
              enabledAtVersion: null,
            });
          }
        }
      },
    }),
    {
      name: 'safe-mode-storage',
      partialize: (state) => ({
        enabled: state.enabled,
        autoEnabled: state.autoEnabled,
        autoEnabledAt: state.autoEnabledAt,
        triggerErrorCount: state.triggerErrorCount,
        enabledAtVersion: state.enabledAtVersion,
      }),
    }
  )
);

/**
 * Hook that returns whether to use safe/native components
 */
export function useIsSafeMode(): boolean {
  return useSafeMode((state) => state.enabled);
}

/**
 * Threshold for auto-enabling safe mode
 */
const AUTO_ENABLE_THRESHOLD = 5; // 5 errors in session

/**
 * Initialize safe mode auto-enable listener and auto-disable checks.
 * Call this once at app startup.
 */
export function initializeSafeModeAutoEnable(): () => void {
  // Check for auto-disable conditions on startup
  useSafeMode.getState().checkAutoDisable();

  // Subscribe to error count for auto-enable
  return recoveredErrorTracker.subscribe((count) => {
    if (count >= AUTO_ENABLE_THRESHOLD) {
      useSafeMode.getState().autoEnable(count);
    }
  });
}

/**
 * Get safe mode status for diagnostics
 */
export function getSafeModeStatus(): {
  enabled: boolean;
  autoEnabled: boolean;
  autoEnabledAt: string | null;
  triggerErrorCount: number;
  enabledAtVersion: string | null;
} {
  const state = useSafeMode.getState();
  return {
    enabled: state.enabled,
    autoEnabled: state.autoEnabled,
    autoEnabledAt: state.autoEnabledAt,
    triggerErrorCount: state.triggerErrorCount,
    enabledAtVersion: state.enabledAtVersion,
  };
}
